import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Mail, Phone, Calendar, User, CheckCircle, X, FileText, Video, Award, MessageSquare } from 'lucide-react';
import axiosInstance from '../utils/axiosInterceptor';
import toast from 'react-hot-toast';

type RatingBarProps = {
  score: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  subtitle?: string;
  type?: "score" | "multipleFaces";
};





export function RatingBar({
  score,
  max = 10,
  size = 90,
  strokeWidth = 6,
  color = "#22c55e", // Tailwind red-500
  subtitle = "Score",
  type = "score",
  onClick
}: RatingBarProps & { onClick?: () => void }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Round score to whole number (no fractions)
  const roundedScore = Math.round(score);
  const safeScore = Math.min(Math.max(roundedScore, 0), max);
  const targetOffset =
    circumference - (safeScore / max) * circumference;

  const [offset, setOffset] = useState(circumference);

  // 🔥 animate on mount / score change
  useEffect(() => {
    const timer = setTimeout(() => {
      setOffset(targetOffset);
    }, 50);

    return () => clearTimeout(timer);
  }, [targetOffset]);

  return (
    <div className={`flex flex-col items-center gap-1 ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      <div
        className="relative flex items-center justify-center"
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          className="-rotate-90"
        >
          {/* background */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="none"
          />

          {/* progress */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{
              transition: "stroke-dashoffset 0.8s ease"
            }}
          />
        </svg>

        <span className="absolute text-sm font-semibold text-gray-800">
          {safeScore}/{max}
        </span>
      </div>

      {/* subtitle */}
      <span className="text-xs text-gray-500">
        {subtitle}
      </span>
    </div>
  );
}

export function CountBar({
  count,
  size = 90,
  bgColor = "#e5e7eb",
  textColor = "#111827",
  onClick,
}: {
  count: number | string;
  size?: number;
  bgColor?: string;
  textColor?: string;
  onClick?: () => void;
}) {
  return (
    <div
      className={`flex items-center justify-center rounded-full ${onClick ? "cursor-pointer" : ""
        }`}
      style={{
        width: size,
        height: size,
        backgroundColor: bgColor,
      }}
      onClick={onClick}
    >
      <span
        className="font-semibold"
        style={{
          fontSize: size * 0.32,
          color: textColor,
        }}
      >
        {count}
      </span>
    </div>
  );
}




const InterviewDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('sessionId');
  const assessmentId = searchParams.get('assessmentId');

  const [loading, setLoading] = useState(true);
  const [candidateData, setCandidateData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'recordings' | 'skills' | 'transcripts'>('summary');

  const [transcripts, setTranscripts] = useState<any>([]);
  const [questions, setQuestions] = useState<any>([]);
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [screenshotsUrl, setScreenshotsUrl] = useState<any>([]);
  const [videoError, setVideoError] = useState<boolean>(false);
  const [summary, setSummary] = useState<string>('');
  const [technicalPositives, setTechnicalPositives] = useState<any>([]);
  const [technicalNegatives, setTechnicalNegatives] = useState<any>([]);

  const [communicationPositives, setCommunicationPositives] = useState<any>([]);
  const [communicationNegatives, setCommunicationNegatives] = useState<any>([]);

  const [recommendations, setRecommendations] = useState<any>([]);
  const [score, setScore] = useState(0);
  const [interviewDuration, setInterviewDuration] = useState<string>('');
  const [actualDuration, setActualDuration] = useState<string>('');
  const [multipleFaces, setMultipleFaces] = useState([]);
  const [showMultipleFacesModal, setShowMultipleFacesModal] = useState(false);
  useEffect(() => {
    if (!sessionId) {
      toast.error('Missing session ID');
      navigate('/dashboard');
      return;
    }

    const load = async () => {
      setLoading(true);
      try {
        await fetchInterviewSummary();
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [sessionId]);

  const fetchInterviewSummary = async () => {
    try {
      const response = await axiosInstance.get(`/interview/summary/${sessionId}`);
      console.log(response.data.data, "response")
      const res = response.data.data;
      setCandidateData((prev: any) => ({
        ...prev,
        ...(res.candidate || {}),
      }));
      setTranscripts(res.transcripts);
      setQuestions(res.report.questions);
      setVideoUrl(res.videoUrl);
      setScreenshotsUrl(res.screenshotsUrl);
      setVideoError(false);
      setScore(res.report.score);
      setTechnicalPositives(res.report.technical.positives);
      setTechnicalNegatives(res.report.technical.negatives);
      setCommunicationPositives(res.report.communication.positives);
      setCommunicationNegatives(res.report.communication.negatives);
      setRecommendations(res.report.recommendations || []);
      setMultipleFaces(res.report.multipleFaces);
      setSummary(res?.report?.summary || '');
      setInterviewDuration(res.report.interviewDuration || '');
      setActualDuration(res.report.actualDuration || '');
      // Reset error state when new video URL is loaded
    } catch (error: any) {
      console.error('Error fetching interview summary:', error);
      toast.error('Failed to fetch interview summary');
    }
  };

  const fetchCandidateDetails = async () => {};

  const formatDate = (date: string | Date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading candidate details...</p>
        </div>
      </div>
    );
  }

  if (!candidateData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <X className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Candidate Not Found</h2>
          <p className="text-gray-600 mb-4">The candidate you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'summary', label: 'Summary', icon: FileText },
    { id: 'recordings', label: 'Recordings', icon: Video },
    { id: 'skills', label: 'Skills', icon: Award },
    { id: 'transcripts', label: 'Transcripts', icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Interview Details</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Section - Candidate Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              {/* Personal Information */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2" />
                  Personal Information
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">First Name</label>
                    <p className="text-gray-900 mt-1">{candidateData.firstName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Name</label>
                    <p className="text-gray-900 mt-1">{candidateData.lastName || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <p className="text-gray-900">{candidateData.email || 'N/A'}</p>
                      {candidateData.email && (
                        <button
                          onClick={() => copyToClipboard(candidateData.email, 'Email')}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy email"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <p className="text-gray-900">{candidateData.phone || 'N/A'}</p>
                      {candidateData.phone && (
                        <button
                          onClick={() => copyToClipboard(candidateData.phone, 'Phone')}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                          title="Copy phone"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Interview Status */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="h-5 w-5 mr-2" />
                  Interview Status
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <div className="mt-1">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${candidateData.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : candidateData.status === 'in_progress'
                          ? 'bg-blue-100 text-blue-800'
                          : candidateData.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                        {candidateData.status === 'completed' && <CheckCircle className="h-4 w-4 mr-1" />}
                        {candidateData.status || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created At</label>
                    <p className="text-gray-900 mt-1 text-sm">{formatDate(candidateData.createdAt)}</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-2">
                  {candidateData.email && (
                    <a
                      href={`mailto:${candidateData.email}`}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Mail className="h-4 w-4" />
                      <span>Send Email</span>
                    </a>
                  )}
                  {candidateData.phone && (
                    <a
                      href={`tel:${candidateData.phone}`}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      <span>Call</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Tabs */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span>{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'summary' && (

                  <div className="space-y-6 min-w-[600px]">

                    {/* Technical Observations */}
                    <section className='flex items-center space-x-2 gap-2'>
                      <RatingBar
                        score={score || 0}
                        color={score >= 7 ? "#22c55e" : score >= 4 ? "#f59e0b" : "#ef4444"} // green-500
                        subtitle="Overall Score"
                      />


                      <RatingBar
                        score={multipleFaces?.length || 0}
                        color="#22c55e" // green-500
                        subtitle="Multiple Faces"
                        onClick={() => multipleFaces?.length > 0 && setShowMultipleFacesModal(true)}
                      />



                    </section>
                    <h4 className="text-base font-semibold text-gray-900">Overall Summary</h4>
                    <div className="space-y-1 text-sm text-gray-500">
                      <p>{summary || 'N/A'}</p>
                      {(interviewDuration || actualDuration) && (
                        <div className="flex flex-wrap gap-4 text-xs text-gray-600 mt-2">
                          {interviewDuration && (
                            <span>
                              <span className="font-medium text-gray-700">Planned Duration:</span>{' '}
                              {interviewDuration}
                            </span>
                          )}
                          {actualDuration && (
                            <span>
                              <span className="font-medium text-gray-700">Actual Duration:</span>{' '}
                              {actualDuration}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="space-y-4">
                      <h4 className="text-base font-semibold text-gray-900">Technical Observations</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Positives - Left */}
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium text-green-700">Positives</h5>
                          <ul className="list-disc list-inside space-y-1">
                            {technicalPositives && technicalPositives.length > 0 ? (
                              technicalPositives.map((positive: string, index: number) => (
                                <li key={`pos-${index}`} className="text-sm text-gray-700">
                                  {positive.trim()}
                                </li>
                              ))
                            ) : (
                              <li className="text-sm text-gray-500">No positive observations</li>
                            )}
                          </ul>
                        </div>

                        {/* Negatives - Right */}
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium text-red-700">Areas for Improvement</h5>
                          <ul className="list-disc list-inside space-y-1">
                            {technicalNegatives && technicalNegatives.length > 0 ? (
                              technicalNegatives.map((negative: string, index: number) => (
                                <li key={`neg-${index}`} className="text-sm text-gray-700">
                                  {negative.trim()}
                                </li>
                              ))
                            ) : (
                              <li className="text-sm text-gray-500">No areas for improvement noted</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Communication Observations */}
                    <div className="space-y-4">
                      <h4 className="text-base font-semibold text-gray-900">Communication Observations</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Positives - Left */}
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium text-green-700">Positives</h5>
                          <ul className="list-disc list-inside space-y-1">
                            {communicationPositives && communicationPositives.length > 0 ? (
                              communicationPositives.map((positive: string, index: number) => (
                                <li key={`comm-pos-${index}`} className="text-sm text-gray-700">
                                  {positive.trim()}
                                </li>
                              ))
                            ) : (
                              <li className="text-sm text-gray-500">No positive observations</li>
                            )}
                          </ul>
                        </div>

                        {/* Negatives - Right */}
                        <div className="space-y-2">
                          <h5 className="text-sm font-medium text-red-700">Areas for Improvement</h5>
                          <ul className="list-disc list-inside space-y-1">
                            {communicationNegatives && communicationNegatives.length > 0 ? (
                              communicationNegatives.map((negative: string, index: number) => (
                                <li key={`comm-neg-${index}`} className="text-sm text-gray-700">
                                  {negative.trim()}
                                </li>
                              ))
                            ) : (
                              <li className="text-sm text-gray-500">No areas for improvement noted</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Recommendations */}
                    <div className="space-y-4">
                      <h4 className="text-base font-semibold text-gray-900">Recommendations</h4>
                      <div className="space-y-2">
                        <ul className="list-disc list-inside space-y-1">
                          {recommendations && recommendations.length > 0 ? (
                            recommendations.map((recommendation: string, index: number) => (
                              <li key={`rec-${index}`} className="text-sm text-gray-700">
                                {recommendation.trim()}
                              </li>
                            ))
                          ) : (
                            <li className="text-sm text-gray-500">No recommendations available</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'recordings' && (
                  <div className="space-y-6 min-w-[600px]">
                    <h3 className="text-lg font-semibold text-gray-900">Recordings</h3>
                    {videoUrl ? (
                      <div className="space-y-6">
                        <div>
                          <label className="text-sm font-medium text-gray-500 mb-2 block">Video Recording</label>
                          {videoError ? (
                            <div className="bg-gray-100 border border-gray-300 rounded-lg p-8 text-center">
                              <Video className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                              <p className="text-gray-700 font-medium mb-1">Video unavailable</p>
                              <p className="text-sm text-gray-500">The video recording could not be loaded. It may be corrupted or the URL is invalid.</p>
                              <a
                                href={videoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-4 inline-block text-sm text-blue-600 hover:text-blue-700"
                              >
                                Try opening video URL directly
                              </a>
                            </div>
                          ) : (
                            <div className="bg-black rounded-lg overflow-hidden">
                              <video
                                src={videoUrl}
                                controls
                                className="w-full h-auto max-h-[600px]"
                                preload="metadata"
                                onError={() => setVideoError(true)}
                                onLoadStart={() => setVideoError(false)}
                              >
                                Your browser does not support the video tag.
                              </video>
                            </div>
                          )}
                        </div>

                        {/* Screenshots Grid */}
                        {screenshotsUrl && screenshotsUrl.length > 0 && (
                          <div>
                            <label className="text-sm font-medium text-gray-500 mb-3 block">Screenshots</label>
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {screenshotsUrl.map((screenshot: any, index: number) => (
                                <div key={index} className="relative group">
                                  <img
                                    src={screenshot.url}
                                    alt={`Screenshot ${index + 1}`}
                                    className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                    onClick={() => window.open(screenshot.url, '_blank')}
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="sans-serif" font-size="14"%3EImage not available%3C/text%3E%3C/svg%3E';
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity rounded-lg flex items-center justify-center">
                                    <span className="text-white text-xs opacity-0 group-hover:opacity-100">Click to view</span>
                                  </div>
                                  {screenshot.timestamp && (
                                    <div className="absolute bottom-1 left-1 right-1">
                                      <p className="text-xs text-white bg-black bg-opacity-50 px-2 py-1 rounded truncate">
                                        {formatDate(screenshot.timestamp)}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No recordings available</p>
                    )}
                  </div>
                )}

                {activeTab === 'skills' && (
                  <div className="space-y-4 min-w-[600px] max-h-[70vh] overflow-y-auto pr-2">
                    <h3 className="text-lg font-semibold text-gray-900">Skills Assessment</h3>
                    {questions && questions.length > 0 ? (
                      <div className="space-y-3">
                        {questions.map((question: any, index: number) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-xl p-4 bg-gradient-to-br from-gray-50 to-white shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div className="flex items-start gap-3">
                                <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700 border border-blue-100">
                                  Q{index + 1}
                                </span>
                                <div>
                                  <p className="text-sm font-semibold text-gray-900 leading-snug">
                                    {question.question}
                                  </p>
                                  {question.skill && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {String(question.skill)
                                        .split(',')
                                        .map((skill: string) => skill.trim())
                                        .filter(Boolean)
                                        .map((skill: string, skillIndex: number) => (
                                          <span
                                            key={`${index}-skill-${skillIndex}`}
                                            className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-100"
                                          >
                                            {skill}
                                          </span>
                                        ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            {question.answer && (
                              <div className="mt-2 rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
                                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                  Candidate Answer
                                </p>
                                <p className="text-sm text-gray-800 leading-relaxed">
                                  {question.answer}
                                </p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No skills assessment data available</p>
                    )}
                  </div>
                )}

                {activeTab === 'transcripts' && (
                  <div className="space-y-4 min-w-[600px]">
                    <h3 className="text-lg font-semibold text-gray-900">Transcripts</h3>
                    {transcripts?.length > 0 ? (
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {transcripts.map((transcript: any, index: number) => (
                          <div key={index} className={`border border-gray-200 rounded-lg p-4 ${transcript.role === "AI" ? "bg-blue-50" : "bg-white"}`}>
                            {transcript.timestamp && <div className="flex items-center justify-between mb-2">
                              <span className="text-xs text-gray-500">
                                {formatDate(transcript.timestamp)}
                              </span>

                            </div>}
                            <p className="text-sm text-gray-900 mb-2">{transcript.question}</p>
                            <p className="text-sm text-gray-900 mb-2">{transcript.answer} </p>

                            {transcript.transcript && <p className="text-sm text-gray-900 mb-2"><span className="font-bold">{transcript.role }</span>: {transcript.transcript} </p>}

                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No transcripts available</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Multiple Faces Screenshots Modal */}
      {showMultipleFacesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Screenshots with Multiple Faces</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {multipleFaces?.length || 0} screenshot{multipleFaces?.length !== 1 ? 's' : ''} detected
                </p>
              </div>
              <button
                onClick={() => setShowMultipleFacesModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Content - Screenshots Grid */}
            <div className="flex-1 overflow-y-auto p-6">
              {multipleFaces && multipleFaces.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {multipleFaces.map((screenshot: any, index: number) => {
                    const imageUrl = screenshot.imgUrl || screenshot.url || screenshot.image || screenshot;
                    const faceCount = screenshot.faceCount || screenshot.analysis?.faceCount || 0;
                    return (
                      <div key={index} className="relative group border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                        <img
                          src={imageUrl}
                          alt={`Multiple faces detected ${index + 1}`}
                          className="w-full h-48 object-cover cursor-pointer"
                          onClick={() => window.open(imageUrl, '_blank')}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23f3f4f6" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%239ca3af" font-family="sans-serif" font-size="14"%3EImage not available%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity flex items-center justify-center">
                          <span className="text-white text-xs opacity-0 group-hover:opacity-100">Click to view full size</span>
                        </div>
                        {screenshot.timestamp && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 p-2">
                            <p className="text-xs text-white">
                              {formatDate(screenshot.timestamp)}
                            </p>
                          </div>
                        )}
                        {faceCount > 1 && (
                          <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded font-medium">
                            {faceCount} face{faceCount !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No screenshots with multiple faces detected</p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowMultipleFacesModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewDetails;
